import { memo } from 'react';

import { FullProject, isProjectActive, projectStatusByCode } from 'entities';

import { Tag, Column, Row, IconButton, Link, ResponsiveRender, Tooltip } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { Deadline, ExpectedDate } from 'pages/Projects/components/Deadline';
import { EntityStatusTag } from 'components/Entities';

import { EarthIcon, ExpandIcon } from 'components/Icons';

import { ProjectTasks } from './Tasks';
import { FreshdeskTicketTag, ProjectTypeTag } from 'pages/Projects/components';

import { useToggle } from 'hooks';

import { cn } from 'styles';
import { getFirstAndLastName } from 'utils/text';
import { getCountryLabel } from 'utils/countries';

interface SingleProjectProps {
  project: FullProject;
  timeParser?: (project: FullProject) => string;
}

function SingleProject({ project, timeParser }: SingleProjectProps) {
  const { id, name, owner, tags, status, tasks, clientName, ticket, countries } = project;

  const { isOn, toggle } = useToggle();

  return (
    <Column
      className="relative overflow-hidden rounded-lg border bg-background p-4 pl-6 shadow-md transition-transform duration-500 animate-in fade-in-0 slide-in-from-bottom-11 hover:scale-[1.008]"
      gap="0"
    >
      <div
        // side decoration
        className="absolute bottom-0 left-0 top-0 w-2"
        style={{ background: projectStatusByCode[status].color }}
      />

      <div
        onClick={tasks.length ? toggle : undefined}
        className={cn(
          'grid grid-cols-[repeat(6,minmax(0,1fr)),40px] items-center gap-y-6',
          'tablet:grid-cols-[repeat(5,minmax(0,1fr)),40px]',
          'mid_tablet:grid-cols-[repeat(4,minmax(0,1fr)),40px]',
          'phone:grid-cols-4 phone:gap-3',
        )}
      >
        <Column
          gap="1.5"
          className={cn(
            //
            'col-span-2 w-full',
            'mid_tablet:col-span-3',
          )}
        >
          <Row flexWrap="wrap" gap="1.5">
            <Link
              to="projectProfile"
              params={{ projectId: id }}
              className="text-lg font-medium hover:underline tablet:text-base"
            >
              {name.replace(/_/g, ' ')}
            </Link>

            {tags
              .slice()
              .sort((a, b) => a.name.length - b.name.length)
              .map((tag) => (
                <Tag key={`${id}-${tag.id}`} {...tag} />
              ))}
          </Row>

          <span className="text-sm font-medium">{clientName}</span>

          <Row className="mt-1 phone:flex-wrap phone:gap-3">
            <FreshdeskTicketTag
              ticket={ticket}
              type="outlined"
              className="border-0 px-0 text-inherit"
            />

            <ProjectTypeTag
              projectType={project.type}
              type="outlined"
              className="border-0 px-0 text-inherit"
            />

            <Tooltip
              ensurePresence
              content={
                <Column gap="1">
                  {countries
                    .map((code) => getCountryLabel(code))
                    .sort()
                    .map((country) => (
                      <span key={country}>{country}</span>
                    ))}
                </Column>
              }
            >
              <Tag
                color="primary"
                type="outlined"
                className="border-0 px-0 text-inherit"
                name={
                  <>
                    <EarthIcon size={14} />
                    {countries.length}
                  </>
                }
              />
            </Tooltip>
          </Row>
        </Column>

        <Row
          gap="2"
          className={cn(
            'col-span-2',
            'tablet:col-span-1',
            'mid_tablet:col-span-3 mid_tablet:col-start-3 mid_tablet:row-start-2 mid_tablet:justify-end',
            'phone:col-start-2',
          )}
        >
          <span className="hidden mid_tablet:inline phone:text-sm small_phone:text-end">
            {getFirstAndLastName(owner.name)}
          </span>

          <UserAvatar user={owner} size={32} className="text-sm" showNameOnHover={false} />

          <span className="mid_tablet:hidden">{getFirstAndLastName(owner.name)}</span>
        </Row>

        <div className="justify-self-start">
          <ResponsiveRender
            defaultContent={<Deadline target={project} text={timeParser?.(project)} />}
            responsiveContent={
              <ExpectedDate
                date={project.expectedDate}
                isActive={isProjectActive(project)}
                className="text-xs"
              />
            }
          />
        </div>

        {/* Shows until phone */}
        <EntityStatusTag
          target="project"
          status={status}
          className={cn(
            'h-8 w-40 shrink px-1',
            'mid_tablet:col-start-4 mid_tablet:row-start-1 mid_tablet:self-start mid_tablet:justify-self-end',
            'px-3 small_tablet:w-auto',
            'phone:hidden',
          )}
        />

        {/* hides on phone */}
        <IconButton
          className="justify-self-end mid_tablet:col-start-5 mid_tablet:row-start-1 mid_tablet:self-start phone:hidden"
          to="projectProfile"
          params={{ projectId: id }}
        >
          <ExpandIcon size={20} />
        </IconButton>

        {/* Shows on phone */}
        <EntityStatusTag
          target="project"
          status={status}
          className="col-start-4 row-start-1 mt-1 hidden w-fit self-start justify-self-end px-2 phone:flex small_phone:text-xs"
          round={false}
        />
      </div>

      <ProjectTasks tasks={tasks} isOpen={isOn} projectStatus={status} />
    </Column>
  );
}

export default memo(SingleProject);
